import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Unlock, AlertTriangle, History, Loader2 } from "lucide-react";

const REASON_PRESETS = [
  "Attendance correction",
  "Missing employee attendance",
  "Transfer correction",
  "Salary correction",
  "Advance correction",
  "Other",
];

/**
 * iter213 — SA/Admin dialog to Unlock a locked payroll month.
 * - Mandatory reason (radio + free-text when "Other").
 * - Extra warning if a FINAL payroll snapshot exists.
 * - After unlock, dependent snapshots become STALE (backend does it).
 */
export function UnlockPayrollDialog({ token, month, center, isSuperAdmin, isAdmin, onUnlocked }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reasonKind, setReasonKind] = useState("Attendance correction");
  const [otherText, setOtherText] = useState("");

  const canSee = !!(isSuperAdmin || isAdmin);
  if (!canSee) return null;
  const scopeLabel = (center && center !== "ALL") ? center : "ALL CENTERS";

  const submit = async () => {
    const reason = reasonKind === "Other"
      ? (otherText || "").trim()
      : reasonKind;
    if (reason.length < 3) {
      toast.error("A reason (min 3 chars) is required to unlock payroll.");
      return;
    }
    // iter217 — extra confirmation for the destructive ALL_CENTERS path
    if (!center || center === "ALL") {
      const ok = window.confirm(
        `You are about to unlock payroll for EVERY eligible centre for ${month}.\n\n` +
        `This affects every centre's attendance edit window. Continue?`);
      if (!ok) return;
    }
    setBusy(true);
    try {
      const payload = (!center || center === "ALL")
        ? { token, month, reason, scope: "ALL_CENTERS" }
        : { token, month, reason, center, scope: "SPECIFIC" };
      const { data } = await api.post("/unlock_payroll", payload);
      const finalWarn = data.was_final_before_unlock
        ? " (FINAL payroll reverted to LIVE — regenerate before re-lock)"
        : "";
      toast.success(`Unlocked ${scopeLabel} · ${month}${finalWarn}`);
      setOpen(false);
      onUnlocked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlock payroll");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="destructive" onClick={() => setOpen(true)}
        data-testid="unlock-payroll-btn">
        <Unlock className="w-3.5 h-3.5 mr-1.5" />
        Unlock {scopeLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Unlock Payroll — {scopeLabel} · {month}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900 text-xs">
              Unlocking this payroll allows attendance and payroll-affecting data to be
              corrected. Existing payroll calculations will be marked <b>STALE</b> and
              must be regenerated before finalisation. If a FINAL snapshot exists it will
              revert to LIVE (previous version is preserved in the lock history).
            </div>
            <div>
              <Label className="text-xs mb-2 block">Reason for Unlock *</Label>
              <RadioGroup value={reasonKind} onValueChange={setReasonKind}
                            className="grid grid-cols-2 gap-1.5">
                {REASON_PRESETS.map(r => (
                  <label key={r} className="flex items-center gap-2 text-xs cursor-pointer">
                    <RadioGroupItem value={r} data-testid={`unlock-reason-${r.replace(/\s+/g, '-')}`} />
                    <span>{r}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            {reasonKind === "Other" && (
              <Input placeholder="Enter reason (min 3 chars)"
                       value={otherText}
                       onChange={e => setOtherText(e.target.value)}
                       data-testid="unlock-reason-other-input" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submit} disabled={busy}
                     data-testid="unlock-confirm-btn">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              <Unlock className="w-3.5 h-3.5 mr-1.5" />
              Confirm Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


export function LockHistoryDialog({ token, month }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/payroll_status", { token, month });
        setRows(data.history || []);
      } catch { setRows([]); }
      setLoading(false);
    })();
  }, [open, token, month]);

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}
        data-testid="lock-history-btn">
        <History className="w-3.5 h-3.5 mr-1.5" />
        View Lock History
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payroll Lock History — {month}</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="p-6 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-purple-600" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4">
              No lock/unlock activity yet.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When (UTC)</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Prev → New</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="whitespace-nowrap font-mono text-[11px]">
                        {(r.at || "").replace("T", " ").slice(0, 19)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={r.action === "UNLOCKED" ? "destructive" : "default"}
                                className="text-[10px]">
                          {r.action}
                          {r.was_final_before_unlock && " (from FINAL)"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.by_name || r.by_mobile || "—"}</TableCell>
                      <TableCell className="text-xs">{r.role || "—"}</TableCell>
                      <TableCell className="text-xs">{r.reason || "—"}</TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">
                        {r.prev_state} → {r.new_state}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
